import { useTheme } from '@emotion/react';
import { Box, useMediaQuery } from '@mui/material';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';

import { fundsSlice } from '!views/Market';
import { marketStockExchangeSlice } from '!views/Market/StockExchange/_marketStockExchange.slice';
import { TrlVariationLabel } from '»components/atoms';
import { menusSlice } from '»components/Menus';
import { useCurrentPage } from '»utils/customHooks';
import { TrlDateFormatted, TrlTimeFormatted } from '»utils/date';
import { getPercentageDecorator } from '»utils/endDecorator';
import { displayNominalValue } from '»utils/nominalValue';
import { getDecimalDigits, TrlNumberFormatted } from '»utils/number';

import { TrlCardNegButtons } from '../TrlCardNegButtons';
import { TrlSimpleChart } from '../TrlSimpleChart';
import { TrlVariation } from '../TrlVariation';
import {
  BottomDetails,
  InlineWrappedData,
  InnerWrapper,
  LabelStyled,
  MainWrapper,
  PropStyled,
  QuotationStyled,
  QuoteLabelStyled,
  SmallerInfo,
  TimeStyled,
  ValueStyled,
  WrapperData,
} from './TrlCardStockExchange.styles';
import { TrlMainDetails } from './TrlMainDetails';

const WrappedData = ({
  firstLabel,
  firstValue,
  secondLabel,
  secondValue,
  show,
  decimalDigits,
}) => (
  <WrapperData show={show}>
    <Box>
      <LabelStyled>
        <FormattedMessage id={firstLabel} />
      </LabelStyled>
      <ValueStyled>
        <TrlNumberFormatted
          value={firstValue}
          fontFamily="Roboto"
          decimalDigits={decimalDigits}
        />
      </ValueStyled>
    </Box>
    <Box>
      <LabelStyled>
        <FormattedMessage id={secondLabel} />
      </LabelStyled>
      <ValueStyled>
        <TrlNumberFormatted
          value={secondValue}
          fontFamily="Roboto"
          decimalDigits={decimalDigits}
        />
      </ValueStyled>
    </Box>
  </WrapperData>
);

WrappedData.propTypes = {
  firstLabel: PropTypes.string.isRequired,
  firstValue: PropTypes.number,
  secondLabel: PropTypes.string.isRequired,
  secondValue: PropTypes.number,
  show: PropTypes.bool,
  decimalDigits: PropTypes.number.isRequired,
};

export const TrlCardStockExchange = ({
  data,
  isCardMobileList,
  isCardMobileDetails,
}) => {
  const theme = useTheme();
  const isScreenWidthBellow700 = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    isMarketStockExchangePage,
    isMarketStockExchangeDetailsPage,
    isFunds,
    isFundsDetailsPage,
  } = useCurrentPage();

  const {
    instrument,
    descr,
    symbol,
    exchange,
    securityExchange,
    exchangeDescr,
    country,
    lastPx,
    lastDateTime,
    totalVolumeTraded,
    nominalValue,
    variationPctg,
    priceRepresentation,
    quantityRepresentation,
    securityType,
    askPx,
    bidPx,
    highPx,
    lowPx,
    isFavourite,
    currency,
    chartData,
    canTrade,
    roundPrice,
    securityDescription,
    subscriptionDate,
    variationPx1d,
  } = data;

  const isMarketStockExchange =
    isMarketStockExchangePage || isMarketStockExchangeDetailsPage;

  const displayedDate =
    isMarketStockExchange || isFunds ? lastDateTime : subscriptionDate;
  const titleVariationPct = isMarketStockExchange
    ? variationPctg
    : variationPx1d;
  const titleExchange = isMarketStockExchange ? exchange : securityExchange;

  const decimalDigits = getDecimalDigits(roundPrice);

  const endDecorator = getPercentageDecorator(priceRepresentation);

  const isCardMobileStockExchangeDetails =
    isCardMobileDetails && isMarketStockExchangeDetailsPage;

  const isCardMobileFundsDetails = isCardMobileDetails && isFundsDetailsPage;

  return (
    <MainWrapper
      onClick={() => {
        if (isCardMobileList && isMarketStockExchangePage) {
          dispatch(
            marketStockExchangeSlice.actions.setSelectedTitle({
              symbol,
              exchange: titleExchange,
              securityType,
            }),
          );

          dispatch(
            menusSlice.actions.setActiveMenuItem({
              activeMenuItem: 'MARKET:BOLSA:DETAILS',
            }),
          );

          navigate('/Market/StockExchange/Details');
        } else if (isCardMobileList && !isMarketStockExchangePage) {
          dispatch(
            fundsSlice.actions.setSelectedFundsManagerTitle({
              symbol,
              securityExchange: titleExchange,
              securityType,
            }),
          );

          dispatch(
            menusSlice.actions.setActiveMenuItem({
              activeMenuItem: 'MARKET:FUNDS:DETAILS',
            }),
          );

          navigate('/Market/Funds/Details');
        }
      }}
      isCardMobileDetails={isCardMobileDetails}
      isCardMobileFundsDetails={isCardMobileFundsDetails}
      variationPctg={titleVariationPct}
    >
      <InnerWrapper id="cardStockExchangeDet" fullWidth={isCardMobileList}>
        <TrlMainDetails
          instrument={instrument}
          symbol={symbol}
          exchange={titleExchange}
          securityType={securityType}
          country={country}
          exchangeDescr={exchangeDescr}
          currency={currency}
          descr={descr || securityDescription}
          priceRepresentation={priceRepresentation}
          favourite={isFavourite}
          isCardMobileList={isCardMobileList}
          isCardMobileDetails={isCardMobileDetails}
        />

        {isCardMobileList && (
          <TrlVariationLabel
            variationPctg={titleVariationPct}
            parent="cardMobileList"
            decimalDigits={decimalDigits}
          />
        )}

        <BottomDetails isCardMobileDetails={isCardMobileDetails}>
          <Box>
            {isCardMobileDetails && (
              <QuoteLabelStyled>
                <FormattedMessage id="TRLT_UI-QUOTE" />
              </QuoteLabelStyled>
            )}
            <QuotationStyled>
              <TrlNumberFormatted
                value={lastPx}
                fontFamily="Roboto"
                endDecorator={endDecorator}
                decimalDigits={decimalDigits}
              />
            </QuotationStyled>

            <SmallerInfo>
              <TimeStyled>
                {displayedDate ? (
                  <>
                    <TrlDateFormatted date={displayedDate} />
                    {' | '}
                    <TrlTimeFormatted
                      value={displayedDate}
                      hour="numeric"
                      minute="numeric"
                      second="numeric"
                    />
                  </>
                ) : (
                  '-'
                )}
              </TimeStyled>
              {!isCardMobileDetails && isMarketStockExchangePage && (
                <PropStyled>
                  <FormattedMessage id="TRLT_UI-VOLUME" />

                  <TrlNumberFormatted
                    value={totalVolumeTraded}
                    fontFamily="Roboto"
                    decimalDigits={decimalDigits}
                  />
                </PropStyled>
              )}
            </SmallerInfo>
          </Box>

          {isCardMobileList && (
            <TrlSimpleChart chartData={chartData} isCardMobileList />
          )}

          {isCardMobileList && (
            <TrlCardNegButtons
              isCardMobileList={isCardMobileList}
              canTrade={canTrade}
              symbol={symbol}
              exchange={titleExchange}
              securityType={securityType}
            />
          )}

          <WrappedData
            firstLabel="TRLT_UI-VOLUME"
            firstValue={totalVolumeTraded}
            secondLabel="TRLT_UI-NOMINAL_VALUE"
            secondValue={displayNominalValue(
              quantityRepresentation,
              priceRepresentation,
              nominalValue,
            )}
            show={isCardMobileStockExchangeDetails}
            decimalDigits={decimalDigits}
          />

          {isCardMobileDetails && isScreenWidthBellow700 && (
            <TrlVariation
              variationPctg={titleVariationPct}
              chartData={chartData}
              isCardMobileList={isCardMobileList}
              isCardMobileDetails={isCardMobileDetails}
              canTrade={canTrade}
              decimalDigits={decimalDigits}
            />
          )}

          {isScreenWidthBellow700 ? (
            <InlineWrappedData>
              <WrappedData
                firstLabel="TRLT_UI-MINIMUM"
                firstValue={lowPx}
                secondLabel="TRLT_UI-MAXIMUM"
                secondValue={highPx}
                show={isCardMobileStockExchangeDetails}
                decimalDigits={decimalDigits}
              />

              <WrappedData
                firstLabel="TRLT_UI-PURCHASE"
                firstValue={bidPx}
                secondLabel="TRLT_UI-SALE"
                secondValue={askPx}
                show={isCardMobileStockExchangeDetails}
                decimalDigits={decimalDigits}
              />
            </InlineWrappedData>
          ) : (
            <>
              <WrappedData
                firstLabel="TRLT_UI-MINIMUM"
                firstValue={lowPx}
                secondLabel="TRLT_UI-MAXIMUM"
                secondValue={highPx}
                show={isCardMobileStockExchangeDetails}
                decimalDigits={decimalDigits}
              />

              <WrappedData
                firstLabel="TRLT_UI-PURCHASE"
                firstValue={bidPx}
                secondLabel="TRLT_UI-SALE"
                secondValue={askPx}
                show={isCardMobileStockExchangeDetails}
                decimalDigits={decimalDigits}
              />
            </>
          )}
        </BottomDetails>
      </InnerWrapper>
      {isCardMobileDetails && !isScreenWidthBellow700 && (
        <TrlVariation
          variationPctg={titleVariationPct}
          chartData={chartData}
          isCardMobileList={isCardMobileList}
          isCardMobileDetails={isCardMobileDetails}
          canTrade={canTrade}
          symbol={symbol}
          exchange={titleExchange}
          securityType={securityType}
          decimalDigits={decimalDigits}
        />
      )}
    </MainWrapper>
  );
};
TrlCardStockExchange.propTypes = {
  data: PropTypes.object.isRequired,
  isCardMobileList: PropTypes.bool,
  isCardMobileDetails: PropTypes.bool,
};
